/**
 * NearMe — /places/near-me
 *
 * Uses browser geolocation → reverse-geocodes via our backend proxy →
 * redirects to the matched geo_places slug. Fully user-driven, no
 * background scanning.
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Navigation, MapPin, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/geo`;

export default function NearMe() {
  const nav = useNavigate();
  const [status, setStatus] = useState("prompt");    // prompt | detecting | matching | error
  const [message, setMessage] = useState("");
  const [match, setMatch] = useState(null);

  const detect = () => {
    if (!navigator.geolocation) {
      setStatus("error"); setMessage("Your browser doesn't support geolocation.");
      return;
    }
    setStatus("detecting");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setStatus("matching");
        try {
          const { latitude: lat, longitude: lon } = pos.coords;
          const r = await fetch(`${API}/nearest?lat=${lat}&lon=${lon}`).then(r => r.json());
          if (r.matched_slug) {
            setMatch(r);
            setTimeout(() => nav(`/places/${r.matched_slug}`), 800);
          } else {
            setStatus("error");
            const bits = [r.raw?.city, r.raw?.state, r.raw?.country].filter(Boolean).join(", ");
            setMessage(`We couldn't match ${bits || "your location"} to a known place yet.`);
          }
        } catch (err) {
          setStatus("error"); setMessage(err.message || "Reverse geocoding failed.");
        }
      },
      (err) => {
        setStatus("error");
        setMessage(
          err.code === 1 ? "Permission denied — enable location access to use Near Me."
          : err.code === 2 ? "Your location isn't available right now."
          : err.code === 3 ? "Location request timed out."
          : "We couldn't get your location."
        );
      },
      { enableHighAccuracy: false, maximumAge: 300_000, timeout: 8000 },
    );
  };

  useEffect(() => { detect(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="places-near-me">
      <span className="inline-flex w-12 h-12 rounded-full bg-blue-50 items-center justify-center">
        <Navigation className="w-6 h-6 text-blue-600" />
      </span>
      <h1 className="mt-4 text-2xl font-black tracking-tight text-slate-900">Places near you</h1>

      {status === "prompt" && (
        <p className="mt-3 text-sm text-slate-600">Detecting your location…</p>
      )}
      {status === "detecting" && (
        <p className="mt-3 text-sm text-slate-600">Waiting for your browser to share your location…</p>
      )}
      {status === "matching" && (
        <>
          <p className="mt-3 text-sm text-slate-600">Matching your location to the ChatsPulse map…</p>
          {match?.raw && (
            <p className="mt-1 text-xs text-slate-400">
              <MapPin className="inline w-3 h-3" /> {[match.raw.city, match.raw.state, match.raw.country].filter(Boolean).join(", ")}
            </p>
          )}
        </>
      )}
      {match && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold" data-testid="near-me-match">
          <MapPin className="w-3 h-3" /> Redirecting to your place…
        </div>
      )}
      {status === "error" && (
        <>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 text-xs font-semibold" data-testid="near-me-error">
            <AlertCircle className="w-3 h-3" /> {message}
          </div>
          <div className="mt-4 flex flex-col gap-2 items-center">
            <button
              type="button"
              onClick={detect}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
              data-testid="near-me-retry"
            >
              Try again
            </button>
            <Link to="/places" className="text-xs text-slate-500 hover:text-blue-700" data-testid="near-me-fallback">
              Or browse all places →
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
